import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LegalFooter from "../components/LegalFooter";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, ShieldAlert, Loader2, MailCheck } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [status, setStatus] = useState("checking"); // checking | valid | invalid | success
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inline "Request a new link" state (shown on the invalid-link screen)
  const [reqEmail, setReqEmail] = useState("");
  const [reqSending, setReqSending] = useState(false);
  const [reqSent, setReqSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-reset-token`, { params: { token } });
        setStatus(data?.valid ? "valid" : "invalid");
      } catch {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not reset password. Please request a new link.";
      toast.error(msg);
      if (err?.response?.status === 400) setStatus("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  const requestNew = async (e) => {
    e.preventDefault();
    if (!reqEmail) return;
    setReqSending(true);
    try {
      await api.post("/auth/forgot-password", { email: reqEmail });
      setReqSent(true);
    } catch {
      // Regardless of a transport error, keep the response generic to avoid enumeration.
      setReqSent(true);
    } finally {
      setReqSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="reset-password-screen">
      <header className="px-6 py-5">
        <Link to="/signin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="reset-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pt-6 pb-16">
        <div className="w-full max-w-md">
          <span className="brand-wordmark text-xs text-muted-foreground">FINALLOOK</span>

          {status === "checking" && (
            <div className="mt-6 flex items-center gap-2 text-muted-foreground text-sm" data-testid="reset-checking">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking your reset link…
            </div>
          )}

          {status === "invalid" && (
            <div className="mt-3" data-testid="reset-invalid">
              <div className="h-12 w-12 rounded-2xl bg-destructive/10 flex items-center justify-center text-destructive mb-4">
                <ShieldAlert className="h-6 w-6" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-3">Link no longer works</h1>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                This reset link is invalid, has expired, or has already been used. Enter your email below and we&apos;ll send a fresh link right away.
              </p>

              {!reqSent ? (
                <form onSubmit={requestNew} className="space-y-4" data-testid="reset-request-new-form">
                  <div className="space-y-2">
                    <Label htmlFor="req-email">Email</Label>
                    <Input
                      id="req-email"
                      type="email"
                      required
                      value={reqEmail}
                      onChange={(e) => setReqEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-11"
                      disabled={reqSending}
                      data-testid="reset-request-new-email"
                      autoFocus
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      disabled={reqSending || !reqEmail}
                      className="rounded-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold w-full"
                      data-testid="reset-request-new-btn"
                    >
                      {reqSending ? "Sending…" : "Send a new reset link"}
                    </Button>
                    <Link to="/signin" className="contents">
                      <Button variant="outline" className="rounded-full h-11 border-border font-semibold w-full" data-testid="reset-invalid-signin-btn">
                        Back to Sign in
                      </Button>
                    </Link>
                  </div>
                </form>
              ) : (
                <div className="rounded-2xl border border-border bg-card p-5" data-testid="reset-request-new-success">
                  <div className="flex items-start gap-3">
                    <MailCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="font-heading font-semibold text-sm">Check your inbox</p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed" data-testid="reset-request-new-success-message">
                        If an account exists for this email, we&apos;ll send password reset instructions. The link expires in 30 minutes.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setReqSent(false); setReqEmail(""); }}
                      className="rounded-full h-10 border-border font-semibold w-full"
                      data-testid="reset-request-new-again-btn"
                    >
                      Send another link
                    </Button>
                    <Link to="/signin" className="contents">
                      <Button className="rounded-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold w-full">
                        Back to Sign in
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === "valid" && (
            <>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-3 mb-2">Choose a new password</h1>
              <p className="text-sm text-muted-foreground mb-8">
                Pick something you&apos;ll actually remember. Minimum 8 characters.
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                <div className="space-y-2">
                  <Label htmlFor="rp-password">New password</Label>
                  <Input
                    id="rp-password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="h-11"
                    data-testid="reset-password-input"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rp-confirm">Confirm new password</Label>
                  <Input
                    id="rp-confirm"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter the password"
                    className="h-11"
                    data-testid="reset-confirm-input"
                  />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-destructive" data-testid="reset-mismatch">Passwords do not match.</p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !password || password !== confirm}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}

          {status === "success" && (
            <div className="mt-3" data-testid="reset-success">
              <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--success))]/15 flex items-center justify-center text-[hsl(var(--success))] mb-4">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-3" data-testid="reset-success-title">
                Password updated successfully.
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                You can now sign in with your new password. The old reset link is now inactive.
              </p>
              <Button
                onClick={() => nav("/signin")}
                className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                data-testid="reset-success-signin-btn"
              >
                Sign in with new password
              </Button>
            </div>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
